import { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Divider, Tabs } from 'antd';
import { CardHeader } from './CardHeader';
import { ToolsPanel } from './ToolsPanel';
import { Fields } from './Fields';
import { FileList } from './FileList';
import StatusBar from '../PhotoMaterial/StatusBar';
import { LinkList } from './SharedLinks';
import { Window } from '../Window';
import {
  CardData,
  IRecordCardController,
} from '../../../store/LinkController/types';
import { RecordCardManager } from '../../../store/shared/RecordCardManager';
import { MapStore } from '../../../store/Map';
import { RecordCardContext } from './context';
import { ShareTab } from './ShareTab';

import './style.scss';

import { ReferencedTables } from '@project/components/Module/RecordCard/ReferencedTables';
import { CardRoute } from '@project/components/Module/RecordCard/CardRoute';

const { TabPane } = Tabs;

export const EXCLUDED_COLUMNS = [
  'id',
  'geom',
  '_created_at',
  '_created_by',
  '_created_by_fullname',
  '_updated_by_fullname',
  '_updated_at',
  '_updated_by',
  '_longitude',
  '_latitude',
];

type RecordCardTab = 'fields' | 'files' | 'links' | 'share';

interface RecordCardProps {
  recordCard: IRecordCardController;
  recordCardManager: RecordCardManager;
  map: MapStore;
  hiddenTabs?: RecordCardTab[];
  left?: number;
  bottom?: number;
}

interface RecordCardViewProps extends RecordCardProps {
  data: CardData;
}

const RecordCardView: React.FC<RecordCardViewProps> = observer(
  function RecordCardView({
    data,
    recordCard,
    recordCardManager,
    map,
    left,
    bottom,
    hiddenTabs = [],
  }) {
    const { tableName, recordId, layerName } = data;
    const [openRoute, setOpenRoute] = useState(false);

    const options = {
      excludedColumn: [...EXCLUDED_COLUMNS],
      connectedLayer: layerName,
    };
    const card = recordCardManager.createCard(tableName, recordId, options);

    const isLoading =
      card.tableInfo.isLoading ||
      card.tableColumns.isLoading ||
      card.attachments.isLoading;

    const error =
      card.tableInfo.error || card.tableColumns.error || card.attachments.error;

    const isLoaded = !isLoading && !error;

    const handleCloseClick = useCallback(() => {
      recordCard.deleteCard();
      recordCardManager.deleteCardFromCollection(card);
    }, [
      recordCardManager.deleteCardFromCollection,
      recordCard.deleteCard,
      card,
    ]);

    const handleCollapse = useCallback(() => {
      recordCardManager.addCardToCollection(card);
      recordCard.deleteCard();
    }, [recordCardManager.addCardToCollection, recordCard.deleteCard, card]);

    const hasAttachments = card.attachments.attachments.length > 0;
    const context = useMemo(() => ({ recordCardManager }), []);

    useEffect(() => {
      setOpenRoute(false);
    }, [data]);

    return (
      <RecordCardContext.Provider value={context}>
        <Window
          className="object-card"
          onClose={handleCloseClick}
          onCollapse={handleCollapse}
          visible={true}
          style={{ left, bottom }}
        >
          {isLoaded ? (
            <>
              <CardHeader card={card} />
              <ToolsPanel
                card={card}
                map={map}
                onRoute={() => setOpenRoute(true)}
              />
              <Divider className="divider" />
              {openRoute ? (
                <CardRoute
                  map={map.mapProperties.map}
                  card={card}
                  onClose={() => setOpenRoute(false)}
                />
              ) : (
                <Tabs
                  styles={{ root: { overflow: 'hidden' } }}
                  tabBarStyle={{ paddingLeft: '10px', paddingRight: '10px' }}
                  defaultActiveKey="32131"
                >
                  {!hiddenTabs.includes('fields') && (
                    <TabPane tab="Информация" key="fields">
                      <Fields card={card} />
                    </TabPane>
                  )}
                  {hasAttachments && !hiddenTabs.includes('files') && (
                    <TabPane tab="Файлы" key="files">
                      <FileList card={card} />
                    </TabPane>
                  )}
                  {!hiddenTabs.includes('links') && (
                    <TabPane tab="Ссылки" key="links">
                      <LinkList card={card} />
                    </TabPane>
                  )}
                  {!hiddenTabs.includes('share') && (
                    <TabPane tab="Поделиться" key="share">
                      <ShareTab card={card} />
                    </TabPane>
                  )}
                  <TabPane tab="Связанные таблицы" key="referenced">
                    <ReferencedTables
                      tableName={data.tableName}
                      recordId={data.recordId}
                    />
                  </TabPane>
                </Tabs>
              )}
            </>
          ) : (
            <div className="object-card-status">
              <StatusBar isLoading={isLoading} error={error} />
              {error && (
                <Button style={{ marginTop: '20px' }} onClick={card.load}>
                  Перезагрузить
                </Button>
              )}
            </div>
          )}
        </Window>
      </RecordCardContext.Provider>
    );
  },
);

export const RecordCard = ({
  recordCard,
  recordCardManager,
  map,
  hiddenTabs,
  left,
  bottom,
}: RecordCardProps) => {
  const renderRecordCard = useCallback(
    (data: CardData) => (
      <RecordCardView
        data={data}
        recordCard={recordCard}
        recordCardManager={recordCardManager}
        map={map}
        left={left}
        bottom={bottom}
        hiddenTabs={hiddenTabs}
      />
    ),
    [recordCardManager, recordCard, map, left, bottom],
  );

  return (
    <RecordCardInitializer
      renderRecordCard={renderRecordCard}
      recordCard={recordCard}
    />
  );
};

interface RecordCardInitializerProps {
  renderRecordCard(v: CardData): JSX.Element;
  recordCard: IRecordCardController;
}

const RecordCardInitializer = observer(
  ({ renderRecordCard, recordCard }: RecordCardInitializerProps) => {
    const data = recordCard.data;

    if (!data) return null;

    return renderRecordCard(data);
  },
);
